import React, { useState, useEffect } from 'react';
import { 
  Download, 
  FileSpreadsheet, 
  Package, 
  TrendingUp,
  Calendar,
  Truck,
  Filter,
  FileText
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose
} from '../components/ui/dialog';
import { reportsAPI, dashboardAPI, projectsAPI, dispatchesAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { toast } from 'sonner';

export default function ReportsPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [dispatchDialogOpen, setDispatchDialogOpen] = useState(false);
  const [dispatchFilters, setDispatchFilters] = useState({
    project_id: '',
    lote_number: '',
    start_date: '',
    end_date: ''
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, projectsRes, dispatchesRes] = await Promise.all([
        dashboardAPI.getStats(),
        projectsAPI.getAll(),
        dispatchesAPI.getAll()
      ]);
      setStats(statsRes.data);
      setProjects(projectsRes.data || []);
      setDispatches(dispatchesRes.data || []);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Get unique lote numbers from dispatches
  const uniqueLotes = [...new Set(dispatches.map(d => d.lote_number).filter(Boolean))];
  
  // Calculate dispatch stats
  const totalDispatches = dispatches.length;
  const thisMonthDispatches = dispatches.filter(d => {
    const dispatchDate = new Date(d.dispatch_date);
    const now = new Date();
    return dispatchDate.getMonth() === now.getMonth() && 
           dispatchDate.getFullYear() === now.getFullYear();
  }).length;

  // Direct download using window.open with authentication
  const downloadWithAuth = async (endpoint, filename) => {
    try {
      toast.info('Generando archivo...');
      
      // Get the token from localStorage (same key as api.js)
      const token = localStorage.getItem('avipork_token');
      if (!token) {
        toast.error('Sesión expirada. Por favor inicia sesión nuevamente.');
        return;
      }
      
      // Make the API call with the token
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Get the blob from response
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Archivo descargado');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Error al descargar archivo');
    }
  };

  const handleExportInventory = () => {
    downloadWithAuth('/api/reports/inventory/excel', `inventario_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleExportDispatches = () => {
    const params = new URLSearchParams();
    if (dispatchFilters.project_id && dispatchFilters.project_id !== 'all') {
      params.append('project_id', dispatchFilters.project_id);
    }
    if (dispatchFilters.lote_number && dispatchFilters.lote_number !== 'all') {
      params.append('lote_number', dispatchFilters.lote_number);
    }
    if (dispatchFilters.start_date) {
      params.append('start_date', dispatchFilters.start_date);
    }
    if (dispatchFilters.end_date) {
      params.append('end_date', dispatchFilters.end_date);
    }
    
    const queryString = params.toString();
    const endpoint = queryString ? `/api/reports/dispatches/excel?${queryString}` : '/api/reports/dispatches/excel';
    downloadWithAuth(endpoint, `despachos_${new Date().toISOString().split('T')[0]}.xlsx`);
    setDispatchDialogOpen(false);
  };

  const handleExportInventoryPdf = () => {
    downloadWithAuth('/api/reports/inventory/pdf', `inventario_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const handleExportDispatchesPdf = () => {
    const params = new URLSearchParams();
    if (dispatchFilters.project_id && dispatchFilters.project_id !== 'all') {
      params.append('project_id', dispatchFilters.project_id);
    }
    if (dispatchFilters.lote_number && dispatchFilters.lote_number !== 'all') {
      params.append('lote_number', dispatchFilters.lote_number);
    }
    if (dispatchFilters.start_date) {
      params.append('start_date', dispatchFilters.start_date);
    }
    if (dispatchFilters.end_date) {
      params.append('end_date', dispatchFilters.end_date);
    }
    
    const queryString = params.toString();
    const endpoint = queryString ? `/api/reports/dispatches/pdf?${queryString}` : '/api/reports/dispatches/pdf';
    downloadWithAuth(endpoint, `despachos_${new Date().toISOString().split('T')[0]}.pdf`);
    setDispatchDialogOpen(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-[#931A25] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Reportes</h1>
        <p className="text-slate-500 mt-1">Exportar datos y reportes del sistema</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#931A25]/10 flex items-center justify-center">
                <Package className="h-5 w-5 text-[#931A25]" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Valor Inventario</p>
                <p className="font-bold text-slate-900">{formatCurrency(stats?.total_inventory_value || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Ventas del Mes</p>
                <p className="font-bold text-slate-900">{formatCurrency(stats?.monthly_sales || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Calendar className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Ventas Hoy</p>
                <p className="font-bold text-slate-900">{formatCurrency(stats?.daily_sales || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                <FileSpreadsheet className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Pagos Pendientes</p>
                <p className="font-bold text-slate-900">{formatCurrency(stats?.pending_payments || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Export Options */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Inventory Report */}
        <Card className="border-slate-200 hover:shadow-md transition-shadow">
          <CardHeader>
            <div className="w-12 h-12 rounded-full bg-[#931A25]/10 flex items-center justify-center mb-2">
              <Package className="h-6 w-6 text-[#931A25]" />
            </div>
            <CardTitle>Reporte de Inventario</CardTitle>
            <CardDescription>
              Exporta el listado completo de materias primas con stock actual, costos y valores totales.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Total Materiales:</span>
                <span className="font-medium">{stats?.total_raw_materials || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Alertas Stock Bajo:</span>
                <span className={`font-medium ${stats?.low_stock_alerts > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {stats?.low_stock_alerts || 0}
                </span>
              </div>
              
              <Tabs defaultValue="excel" className="w-full mt-4">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="excel" className="flex items-center gap-1 text-xs">
                    <FileSpreadsheet className="h-3 w-3" />Excel
                  </TabsTrigger>
                  <TabsTrigger value="pdf" className="flex items-center gap-1 text-xs">
                    <FileText className="h-3 w-3" />PDF
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="excel" className="mt-2">
                  <Button 
                    className="w-full bg-[#931A25] hover:bg-[#7A151F]"
                    onClick={handleExportInventory}
                    data-testid="export-inventory-excel-btn"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Descargar Excel
                  </Button>
                </TabsContent>
                <TabsContent value="pdf" className="mt-2">
                  <Button 
                    className="w-full bg-red-600 hover:bg-red-700"
                    onClick={handleExportInventoryPdf}
                    data-testid="export-inventory-pdf-btn"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Descargar PDF
                  </Button>
                </TabsContent>
              </Tabs>
            </div>
          </CardContent>
        </Card>

        {/* Dispatches Report */}
        <Card className="border-slate-200 hover:shadow-md transition-shadow">
          <CardHeader>
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-2">
              <Truck className="h-6 w-6 text-green-600" />
            </div>
            <CardTitle>Reporte de Despachos</CardTitle>
            <CardDescription>
              Exporta el historial de despachos con filtros por proyecto, lote y fecha.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Total Despachos:</span>
                <span className="font-medium">{totalDispatches}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Este Mes:</span>
                <span className="font-medium">{thisMonthDispatches}</span>
              </div>
              <Dialog open={dispatchDialogOpen} onOpenChange={setDispatchDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    className="w-full mt-4 bg-green-600 hover:bg-green-700"
                    data-testid="export-dispatches-btn"
                  >
                    <Filter className="h-4 w-4 mr-2" />
                    Exportar con Filtros
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Filter className="h-5 w-5 text-green-600" />
                      Filtros del Reporte
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Proyecto</Label>
                      <Select 
                        value={dispatchFilters.project_id} 
                        onValueChange={(value) => setDispatchFilters(prev => ({...prev, project_id: value}))}
                      >
                        <SelectTrigger data-testid="filter-project-select">
                          <SelectValue placeholder="Todos los proyectos" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos los proyectos</SelectItem>
                          {projects.map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Lote</Label>
                      <Select 
                        value={dispatchFilters.lote_number} 
                        onValueChange={(value) => setDispatchFilters(prev => ({...prev, lote_number: value}))}
                      >
                        <SelectTrigger data-testid="filter-lote-select">
                          <SelectValue placeholder="Todos los lotes" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos los lotes</SelectItem>
                          {uniqueLotes.map(lote => (
                            <SelectItem key={lote} value={lote}>{lote}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Fecha Inicio</Label>
                        <Input
                          type="date"
                          value={dispatchFilters.start_date}
                          onChange={(e) => setDispatchFilters(prev => ({...prev, start_date: e.target.value}))}
                          data-testid="filter-start-date"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Fecha Fin</Label>
                        <Input
                          type="date"
                          value={dispatchFilters.end_date}
                          onChange={(e) => setDispatchFilters(prev => ({...prev, end_date: e.target.value}))}
                          data-testid="filter-end-date"
                        />
                      </div>
                    </div>
                  </div>
                  <DialogFooter className="flex-col sm:flex-row gap-2">
                    <DialogClose asChild>
                      <Button variant="outline">Cancelar</Button>
                    </DialogClose>
                    <Button 
                      className="bg-green-600 hover:bg-green-700"
                      onClick={handleExportDispatches}
                      data-testid="confirm-export-dispatches-excel-btn"
                    >
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      Excel
                    </Button>
                    <Button 
                      className="bg-red-600 hover:bg-red-700"
                      onClick={handleExportDispatchesPdf}
                      data-testid="confirm-export-dispatches-pdf-btn"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      PDF
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>

        {/* Coming Soon - Production Report */}
        <Card className="border-slate-200 opacity-75">
          <CardHeader>
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-2">
              <FileSpreadsheet className="h-6 w-6 text-blue-600" />
            </div>
            <CardTitle>Reporte de Producción</CardTitle>
            <CardDescription>
              Exporta el historial de producción con consumo de materiales y costos.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Incluye:</span>
                <span className="font-medium">Órdenes</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Formato:</span>
                <span className="font-medium">Excel (.xlsx)</span>
              </div>
              <Button 
                className="w-full mt-4"
                variant="outline"
                disabled
              >
                <Download className="h-4 w-4 mr-2" />
                Próximamente
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Card */}
      <Card className="border-slate-200 bg-slate-50">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-[#E97171]/20 flex items-center justify-center flex-shrink-0">
              <FileSpreadsheet className="h-5 w-5 text-[#931A25]" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Sobre los Reportes</h3>
              <p className="text-sm text-slate-600 mt-1">
                Los reportes se generan en formato Excel (.xlsx) compatible con Microsoft Excel, 
                Google Sheets y LibreOffice Calc. Las facturas individuales pueden descargarse 
                en formato PDF desde el módulo de Facturación.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
